/** Einstellungen › Preise & Miete (Screen Einst-Preise, MVP-Teil): Preismodell, Provision, Regalklassen, Kaution, Miet-Abo. Langzeitrabatte/Kurzzeit/Gutscheine sind V2. */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import type { LadenEinstellungen } from "@shared/schemas/laden";
import { useLaden, type LadenDaten } from "./api";
import { useRegalklasseAendern, useRegalklasseAnlegen, useRegalklassen, type KlasseZeile, type RegalklasseEingabe } from "./regale-api";

export function EinstellungenPreise() {
  const laden = useLaden();
  return (
    <>
      <PageHeader kicker="Einstellungen" title="Preise und Miete" sub="Preismodell, Provision, Regalklassen, Kaution – gilt für neue Buchungen" />
      <Seitenzustand query={laden}>{(d) => <PreiseFormular daten={d} />}</Seitenzustand>
      <RegalklassenKarte />
    </>
  );
}

function PreiseFormular({ daten }: { daten: LadenDaten }) {
  const qc = useQueryClient();
  const e = daten.einstellungen;
  const [form, setForm] = useState({ modell: e.preise.modell, provision: String(e.preise.provisionProzent), kautionAn: e.preise.kautionCent > 0, kaution: (e.preise.kautionCent / 100).toFixed(2).replace(".", ","), kautionVerrechnen: e.preise.kautionVerrechnen, aboErlaubt: e.preise.aboErlaubt, frist: String(e.preise.kuendigungsfristWochen), kommissionAn: e.kommission.aktiv, kommissionProvision: String(e.kommission.provisionProzent) });
  const speichern = useMutation({ mutationFn: (neu: LadenEinstellungen) => rufe(api.api.laden.einstellungen.$put({ json: neu })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const [erfolg, setErfolg] = useState(false);
  useEffect(() => {
    if (!erfolg) return;
    const t = setTimeout(() => setErfolg(false), 4000);
    return () => clearTimeout(t);
  }, [erfolg]);

  function absenden(ev: FormEvent) {
    ev.preventDefault();
    const neu: LadenEinstellungen = {
      ...e,
      preise: {
        modell: form.modell,
        provisionProzent: form.modell === "miete" ? 0 : Math.max(0, Math.min(100, Number(form.provision.replace(",", ".")) || 0)),
        kautionCent: form.kautionAn ? (parseEuro(form.kaution) ?? 0) : 0,
        kautionVerrechnen: form.kautionVerrechnen,
        aboErlaubt: form.aboErlaubt,
        kuendigungsfristWochen: form.frist === "4" ? 4 : 2,
      },
      kommission: { aktiv: form.kommissionAn, provisionProzent: Math.max(0, Math.min(100, Number(form.kommissionProvision.replace(",", ".")) || 0)) },
    };
    speichern.mutate(neu, { onSuccess: () => setErfolg(true) });
  }

  const modelle: [LadenEinstellungen["preise"]["modell"], string, string][] = [
    ["miete", "Nur Miete", "Feste Regalmiete, Verkäufe gehen voll an die Mieter"],
    ["provision", "Nur Provision", "Keine Miete, der Laden behält einen Anteil je Verkauf"],
    ["beides", "Miete und Provision", "Günstigere Miete plus kleiner Anteil je Verkauf"],
  ];

  return (
    <form className="app-form" onSubmit={absenden} noValidate>
      {erfolg ? <Notice tone="success" title="Gespeichert">Gilt für neue Buchungen und Verlängerungen. Laufende Mieten behalten ihren Preis.</Notice> : null}
      {speichern.isError ? <Notice tone="error">{speichern.error instanceof ApiFehler ? speichern.error.message : "Speichern hat nicht geklappt."}</Notice> : null}
      <Card title="Preismodell">
        <div className="app-option-liste">
          {modelle.map(([k, t, sub]) => (
            <button key={k} type="button" className={["sc-option", form.modell === k && "is-on"].filter(Boolean).join(" ")} onClick={() => setForm({ ...form, modell: k })}>
              <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">{t}</span><span className="sc-muted sc-small">{sub}</span></span>
            </button>
          ))}
        </div>
        {form.modell !== "miete" ? (
          <FormGrid>
            <TextField label="Provision je Verkauf" value={form.provision} onChange={(ev) => setForm({ ...form, provision: ev.target.value })} inputMode="decimal" suffix="%" hint="Wird je Position halb aufgerundet vom Erlös abgezogen" />
          </FormGrid>
        ) : null}
      </Card>
      <Card title="Kaution">
        <label className="sc-check"><input type="checkbox" checked={form.kautionAn} onChange={(ev) => setForm({ ...form, kautionAn: ev.target.checked })} /> Kaution bei neuer Miete verlangen</label>
        {form.kautionAn ? (
          <FormGrid>
            <TextField label="Höhe der Kaution" value={form.kaution} onChange={(ev) => setForm({ ...form, kaution: ev.target.value })} inputMode="decimal" suffix="€" />
          </FormGrid>
        ) : null}
        <label className="sc-check"><input type="checkbox" checked={form.kautionVerrechnen} onChange={(ev) => setForm({ ...form, kautionVerrechnen: ev.target.checked })} /> Beim Auszug mit Guthaben verrechnen (Schlussabrechnung, Etappe 6)</label>
      </Card>
      <Card title="Miet-Abo">
        <label className="sc-check"><input type="checkbox" checked={form.aboErlaubt} onChange={(ev) => setForm({ ...form, aboErlaubt: ev.target.checked })} /> Miet-Abo anbieten (läuft weiter, bis gekündigt wird)</label>
        <FormGrid>
          <Select label="Kündigungsfrist" value={form.frist} onChange={(ev) => setForm({ ...form, frist: ev.target.value })} disabled={!form.aboErlaubt}>
            <option value="2">2 Wochen zum Monatsende</option>
            <option value="4">4 Wochen zum Monatsende</option>
          </Select>
        </FormGrid>
      </Card>
      <Card title="Kommission">
        <label className="sc-check"><input type="checkbox" checked={form.kommissionAn} onChange={(ev) => setForm({ ...form, kommissionAn: ev.target.checked })} /> Ware ohne Regal annehmen (Kommission): Artikel bekommen den Code Mieter-000-Laufnummer, keine Regalmiete, eigene Provision</label>
        {form.kommissionAn ? (
          <FormGrid>
            <TextField label="Provision bei Kommission" value={form.kommissionProvision} onChange={(ev) => setForm({ ...form, kommissionProvision: ev.target.value })} inputMode="decimal" suffix="%" hint="Gilt für alle Kommissionsartikel; annehmen unter Artikel und Etiketten" />
          </FormGrid>
        ) : null}
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>Speichern</Button>
      </div>
    </form>
  );
}

/** Regalklassen pflegen – auch im Einrichtungsassistenten. */
export function RegalklassenKarte({ kompakt }: { kompakt?: boolean }) {
  const klassen = useRegalklassen();
  const [dialog, setDialog] = useState<null | { klasse?: KlasseZeile }>(null);
  return (
    <Card title="Regalklassen" actions={<Button variant="secondary" icon="plus" onClick={() => setDialog({})}>Klasse hinzufügen</Button>} flush>
      {!kompakt ? <p className="sc-muted" style={{ padding: "0 var(--space-4) var(--space-3)" }}>Jede Klasse hat einen Preis je Woche und/oder je Monat. Regale bekommen eine Klasse; die Buchung schlägt den Preis vor.</p> : null}
      <Seitenzustand query={klassen} lines={2}>
        {(d) =>
          d.klassen.length === 0 ? (
            <p className="sc-muted" style={{ padding: "0 var(--space-4) var(--space-4)" }}>Noch keine Klasse – z. B. „Standard“ mit 39,00 € je Monat.</p>
          ) : (
            <div className="app-tabellen-scroll">
              <table className="rg-table">
                <thead><tr><th>Klasse</th><th>Größe</th><th className="is-num">Je Woche</th><th className="is-num">Je Monat</th><th className="is-num">Regale</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {d.klassen.map((k) => (
                    <tr key={k.id}>
                      <td className="sc-strong">{k.name}</td>
                      <td>{k.groesseText || "–"}</td>
                      <td className="is-num">{k.preisWocheCent !== null ? formatEuro(k.preisWocheCent) : "–"}</td>
                      <td className="is-num">{k.preisMonatCent !== null ? formatEuro(k.preisMonatCent) : "–"}</td>
                      <td className="is-num">{k.anzahlRegale}</td>
                      <td>{k.aktiv ? <StatusBadge status="paid">Aktiv</StatusBadge> : <StatusBadge status="neutral">Ausgeblendet</StatusBadge>}</td>
                      <td><Button variant="quiet" icon="pencil" onClick={() => setDialog({ klasse: k })}>Bearbeiten</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Seitenzustand>
      {dialog ? <KlasseFormular klasse={dialog.klasse} onClose={() => setDialog(null)} /> : null}
    </Card>
  );
}

function KlasseFormular({ klasse, onClose }: { klasse?: KlasseZeile; onClose: () => void }) {
  const anlegen = useRegalklasseAnlegen();
  const aendern = useRegalklasseAendern();
  const [form, setForm] = useState({ name: klasse?.name ?? "", groesseText: klasse?.groesseText ?? "", woche: klasse?.preisWocheCent !== null && klasse?.preisWocheCent !== undefined ? (klasse.preisWocheCent / 100).toFixed(2).replace(".", ",") : "", monat: klasse?.preisMonatCent !== null && klasse?.preisMonatCent !== undefined ? (klasse.preisMonatCent / 100).toFixed(2).replace(".", ",") : "", aktiv: klasse?.aktiv ?? true });
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const woche = form.woche.trim() ? parseEuro(form.woche) : null;
    const monat = form.monat.trim() ? parseEuro(form.monat) : null;
    if (!form.name.trim()) return setFehler("Bitte einen Namen eingeben.");
    if (woche === null && monat === null) return setFehler("Mindestens ein Preis (je Woche oder je Monat).");
    const d: RegalklasseEingabe = { name: form.name.trim(), groesseText: form.groesseText.trim(), preisWocheCent: woche, preisMonatCent: monat, reihenfolge: klasse?.reihenfolge ?? 0, aktiv: form.aktiv };
    try {
      if (klasse) await aendern.mutateAsync({ id: klasse.id, ...d });
      else await anlegen.mutateAsync(d);
      onClose();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
    }
  }

  return (
    <Dialog open title={klasse ? `Klasse „${klasse.name}“` : "Regalklasse anlegen"} size="sm" onClose={onClose} actions={<><Button variant="quiet" onClick={onClose}>Abbrechen</Button><Button variant="primary" type="submit" form="klasse-form" loading={anlegen.isPending || aendern.isPending}>Speichern</Button></>}>
      <form id="klasse-form" className="app-form" onSubmit={absenden} noValidate>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Standard, Premium, Kassennähe" autoFocus />
        <TextField label="Größe (Text)" value={form.groesseText} onChange={(e) => setForm({ ...form, groesseText: e.target.value })} placeholder="z. B. 4 Böden, 80 × 40 cm" />
        <FormGrid>
          <TextField label="Preis je Monat" value={form.monat} onChange={(e) => setForm({ ...form, monat: e.target.value })} inputMode="decimal" suffix="€" placeholder="leer = nicht buchbar" />
          <TextField label="Preis je Woche" value={form.woche} onChange={(e) => setForm({ ...form, woche: e.target.value })} inputMode="decimal" suffix="€" placeholder="leer = nicht buchbar" />
        </FormGrid>
        {klasse ? <label className="sc-check"><input type="checkbox" checked={form.aktiv} onChange={(e) => setForm({ ...form, aktiv: e.target.checked })} /> Aktiv (für neue Regale wählbar)</label> : null}
      </form>
    </Dialog>
  );
}
